import React, { forwardRef, useContext, useEffect, useState } from 'react';
import { Form, FormControlProps } from 'react-bootstrap';
import { ApplicationContext } from '../../../../contexts/ApplicationContext';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { toAmount } from '../../../../utils/toAmount';
import { planChargeBreakdown } from '../../../../utils/planCashDiscount';
import { ContentContext } from '../Context/ContentContext';
import { CouponContext } from '../Context/CouponContext';
import { OrderBumpContext } from '../Context/OrderBumpContext';
import { PlanContext } from '../Context/PlanContext';
import { useInstallmentTax } from '../hooks/useInstallmentTax';

interface IProps extends FormControlProps {
	customPrice?: number;
	maxInstallment?: number;
	setValue: (value: number) => void;
}

const InstallmentsInput = forwardRef(
	({ customPrice, maxInstallment, setValue, ...props }: IProps, ref) => {
		const [hasTaxes, setHasTaxes] = useState<boolean>(true);

		const { orderBumps } = useContext(OrderBumpContext);
		const bumpsTotal = orderBumps.reduce(
			(sum, bump) => sum + toAmount(bump.content.pricing?.price),
			0
		);
		const { finance } = useContext(ApplicationContext);

		const { plan } = useContext(PlanContext);
		const { currentPrice, content } = useContext(ContentContext);
		const { coupon } = useContext(CouponContext);

		const { calculate, installments } = useInstallmentTax();

		useEffect(() => {
			const pricingSchema = plan
				? plan?.pricingSchema
				: content?.pricing.pricingSchema;

			const isAnticipationEnabled =
				finance?.isAnticipationEnabled ?? true;
			// Campo vazio = a plataforma paga os juros, como o pay cobra. Supor
			// CUSTOMER mostrava ao comprador juros que não seriam cobrados.
			const anticipationResponsible =
				pricingSchema?.anticipationResponsible ?? 'SELLER';

			const hasTaxes =
				!content?.pricing.isSmartInstallmentEnabled &&
				isAnticipationEnabled &&
				anticipationResponsible === 'CUSTOMER';

			setHasTaxes(hasTaxes);
		}, [plan, content, finance]);

		useEffect(() => {
			if (!currentPrice && customPrice) return;

			const isSmartInstallment =
				content?.pricing.isSmartInstallmentEnabled;

			// Plano com cupom: parcela-se o preço JÁ com o desconto, como o pay
			// cobra o ciclo (`cycleAmountWithCoupon`). Antes ia o preço cheio do
			// plano — o resumo mostrava R$ 7,00 e as parcelas, 1x de R$ 397,00.
			const planPlan = plan
				? { ...plan, price: toAmount(plan.price) }
				: null;
			const planPrice = planPlan
				? planChargeBreakdown(planPlan, coupon, false).total
				: 0;
			// 1x do plano: desconto à vista (se o ciclo aceitar) e depois o cupom — a conta do pay.
			const planCashPrice =
				planPlan && !customPrice
					? planChargeBreakdown(planPlan, coupon, true).total +
					  bumpsTotal
					: undefined;

			calculate(
				// `toAmount`: `price` vem como STRING da API e `+` concatenaria —
				// `'397' + 0 + 0` virava `'39700'`, e a 1x aparecia como R$ 39.700,00.
				toAmount(customPrice || (plan ? planPrice : currentPrice)) +
					bumpsTotal +
					(isSmartInstallment ? toAmount(coupon?.flatDiscount) : 0),
				maxInstallment || 12,
				hasTaxes,
				coupon?.flatDiscount || 0,
				planCashPrice
			);
		}, [currentPrice, customPrice, plan, hasTaxes, orderBumps, coupon]);

		// Desconto à vista do plano, dito NA opção 1x: sem isto o comprador via "1x de R$ 10.964,00"
		// e um total de R$ 11.964,00 sem saber de onde vinha a diferença.
		const cashDiscount =
			plan && !customPrice
				? planChargeBreakdown(
						{ ...plan, price: toAmount(plan.price) },
						coupon,
						true
				  ).cashDiscount
				: 0;

		// Padrão = MENOR parcelamento disponível (1x), não o maior.
		//
		// Antes era `installments.length`: o comprador já encontrava o número
		// MÁXIMO de parcelas selecionado ao abrir o formulário — e, com os juros
		// de antecipação por conta dele (`hasTaxes` acima), pagava o total mais
		// caro sem ter escolhido nada. Agora o padrão é o mais barato, e
		// parcelar passa a ser um ato deliberado do comprador.
		//
		// É a PRIMEIRA opção da lista, e não o literal `1`, porque
		// `useInstallmentTax` filtra por `installmentsSchema.isActive`: se o
		// lojista desativar a 1x, ela não existe na lista, e fixar `1`
		// selecionaria uma opção ausente. A lista é montada em ordem crescente
		// (`for (let i = 1; …)`), então a primeira é sempre a menor disponível.
		const parcelaPadrao = installments[0]?.number;

		useEffect(() => {
			if (!parcelaPadrao) return;

			setValue(parcelaPadrao);
		}, [installments]);

		return (
			<Form.Control as="select" {...props} ref={ref} size="lg">
				{installments.map(({ number, price, label }) => (
					<option
						key={number}
						value={number}
						selected={number === parcelaPadrao}
					>
						{label ||
							(number === 1 && cashDiscount > 0
								? `1x de ${formatCurrency(
										price
								  )} · à vista, com ${formatCurrency(
										cashDiscount
								  )} de desconto`
								: `${number}x de ${formatCurrency(price)}`)}
					</option>
				))}
			</Form.Control>
		);
	}
);

InstallmentsInput.displayName = 'InstallmentsInput';

export { InstallmentsInput };
